import './AutonomousMonitorDetails.scss';
import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { useRecoilValue } from 'recoil';

import {
  Button,
  Grid,
  GridItem,
  Heading,
  Icons,
  LineChart,
  PendingContent,
  Space,
  Spinner,
  Status,
} from 'plume-ui';
import { HeadingStyles } from 'plume-ui/dist/components/Heading/Heading';

import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useFetch } from 'plume-web-utils';
import { UseFetchCacheKeys } from '../../../cacheConfig';

import ErrorBlock from '../../../components/ErrorBlock/ErrorBlock';
import { SHORT_DATE_FORMAT, SHORT_DATE_TIME_FORMAT } from '../../../config';
import { DependencyContainer } from '../../../DependencyContainer';
import { useFeaturePermission } from '../../../hooks/useFeaturePermission';
import { activePartnerAtom } from '../../../state';
import { Permission } from '../../../userPermissionConfig';
import { getFrontlineUrl } from '../../../utils/environments';
import InfoCard, {
  InfoCardGridItem,
} from '../components/AutonomousMonitorDetails/InfoCard';
import LocationsTables from '../components/AutonomousMonitorDetails/LocationsTables';
import LeafletMap from '../components/LeafletMap';
import { LeafletMapMarkerGroup } from '../components/LeafletMap/types';
import SupportOutreachModal from '../components/SupportOutreach/SupportOutreachModal';
import {
  getAutonomousMonitorHourlyFreq,
  getAutonomousMonitorLocations,
} from '../services/monitorQueries';
import { OutreachEnum } from '../types';

dayjs.extend(utc);

const { monitorsService } = new DependencyContainer();

const AutonomousMonitorDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { monitorId } = useParams<{ monitorId: string }>();
  const { hasPermission } = useFeaturePermission();
  const activePartner = useRecoilValue(activePartnerAtom);

  const [supportOutreachModalOpen, setSupportOutreachModalOpen] = useState(
    false,
  );

  const {
    data: monitor,
    isLoading: loadingMonitor,
    error: monitorError,
  } = useFetch(
    async () => {
      if (!activePartner?.id) return;
      return await monitorsService.getAutonomousMonitor(
        activePartner.id,
        monitorId,
      );
    },
    {
      skip: !activePartner?.id || !monitorId,
      cacheKey: UseFetchCacheKeys.AutonomousMonitor,
      dependencies: [activePartner?.id, monitorId],
    },
  );

  const startDate = useMemo(
    () => dayjs().subtract(7, 'days').startOf('day').toISOString(),
    [],
  );
  const endDate = useMemo(() => dayjs().endOf('day').toISOString(), []);

  const { data: monitorGraphData, loading: loadingGraphData } = useQuery(
    getAutonomousMonitorHourlyFreq(monitorId, startDate, endDate),
    {
      skip: !monitorId,
    },
  );

  // TODO look for frequencyInHours
  const graphData = useMemo(() => {
    if (!monitorGraphData) return [];
    return monitorGraphData.points.red
      .map((point, index) => ({
        outlier: point.location_count,
        highNormal: monitorGraphData.points.yellow[index].location_count,
        xAxisLabel: dayjs(point.ts)
          .local()
          .format(
            (monitor?.frequencyInHours || 0) < 24
              ? SHORT_DATE_TIME_FORMAT
              : SHORT_DATE_FORMAT,
          ),
      }))
      .reverse();
  }, [monitorGraphData, monitor?.frequencyInHours]);

  const graphGroups = useMemo(
    () => [
      {
        label: t('monitors.autonomous.locationStatusTypes.highNormal'),
        stroke: '#ffc501',
        key: 'highNormal',
      },
      {
        label: t('monitors.autonomous.locationStatusTypes.outlier'),
        stroke: '#dc0462',
        key: 'outlier',
      },
    ],
    [t],
  );

  const latestData = graphData[graphData.length - 1];

  const {
    data: locationData,
    loading: loadingLocations,
    error: locationError,
  } = useQuery(getAutonomousMonitorLocations(monitorId), {
    skip: !monitorId,
  });

  const getColorForOutreachStatus = (status?: keyof typeof OutreachEnum) => {
    switch (status) {
      case 'active':
        return 'ok';
      default:
        return undefined;
    }
  };

  const onClickMarker = (locationId: string) => {
    window.open(`${getFrontlineUrl()}/location/${locationId}`, '_blank');
  };

  const infoGridData: InfoCardGridItem[] = [
    {
      label: t('monitors.autonomous.monitorDetails.monitoredIssue'),
      value: monitor?.name,
    },
    {
      label: t('monitors.autonomous.monitorDetails.highNormalLocations'),
      value: !loadingGraphData ? (
        `${latestData?.highNormal?.toLocaleString() || '-'}`
      ) : (
        <Spinner visible size="xsmall" />
      ),
    },
    {
      label: t('monitors.autonomous.monitorDetails.outlierLocations'),
      value: !loadingGraphData ? (
        `${latestData?.outlier?.toLocaleString() || '-'}`
      ) : (
        <Spinner visible size="xsmall" />
      ),
    },
    {
      label: t('monitors.monitorInfo.supportOutreach'),
      value: (
        <div className="AutonomousMonitorDetails__outreachStatus">
          <Status
            label={t(
              `monitors.outreachStatuses.${monitor?.outreach.supportEmail?.status}`,
            )}
            color={getColorForOutreachStatus(
              monitor?.outreach.supportEmail?.status,
            )}
          />
          {hasPermission(Permission.EditSupportEmailOutreach) && (
            <div
              onClick={() => setSupportOutreachModalOpen(true)}
              role="button"
              className="AutonomousMonitorDetails__outreachEdit"
            >
              {t('edit')}
            </div>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  }, []);

  const markerGroups: LeafletMapMarkerGroup[] = useMemo(() => {
    if (!locationData) return [];
    return [
      {
        color: '#ffc501', // Warning (yellow) base color
        heatmap: true,
        heatmapConfig: {
          minOpacity: 0.3,
          gradient: {
            0.0: 'rgba(255, 239, 186, 0)',
            0.2: 'rgba(255, 223, 128, 0.4)',
            0.4: 'rgba(255, 211, 77, 0.6)',
            0.6: 'rgba(255, 197, 1, 0.8)',
            0.8: 'rgba(255, 180, 0, 0.9)',
            1.0: 'rgb(255, 165, 0)',
          },
        },
        markers: locationData.locations.yellow.map((location) => ({
          lat: location.latitude || 0,
          long: location.longitude || 0,
          tooltip: location.location_id,
          onClick: () => onClickMarker(location.location_id),
        })),
      },
      {
        color: '#dc0462', // Alert (red) base color
        heatmap: true,
        heatmapConfig: {
          minOpacity: 0.3,
          gradient: {
            0.0: 'rgba(255, 214, 220, 0)',
            0.2: 'rgba(255, 174, 186, 0.4)',
            0.4: 'rgba(255, 123, 133, 0.6)',
            0.6: 'rgba(255, 71, 120, 0.8)',
            0.8: 'rgba(220, 4, 98, 0.9)',
            1.0: 'rgb(180, 0, 60)',
          },
        },
        markers: locationData.locations.red.map((location) => ({
          lat: location.latitude || 0,
          long: location.longitude || 0,
          tooltip: location.location_id,
          onClick: () => onClickMarker(location.location_id),
        })),
      },
    ];
  }, [locationData]);

  const totalMarkers =
    (locationData?.locations.red?.length || 0) +
    (locationData?.locations.yellow?.length || 0);

  if (loadingMonitor)
    return (
      <div data-testid="loader" className="AutonomousMonitorDetails__loading">
        <Spinner visible />
      </div>
    );

  return (
    <div className="AutonomousMonitorDetails">
      <div className="AutonomousMonitorDetails__header">
        <Button
          onClick={() => {
            history.goBack();
          }}
          styleVariant="tertiary-grey"
        >
          <Icons.ArrowLeftIcon width={16} />
          {t('actions.back')}
        </Button>
      </div>

      {!monitorError && (
        <Grid>
          <GridItem colSpan="12" tabletColSpan="12">
            <Space size="l" />
            <Heading
              classes={(current: HeadingStyles) => ({
                root: `${current.root} AppLayout__header AutonomousMonitorDetails__title`,
              })}
              size="xxxxl"
              medium
            >
              {monitor?.name}
            </Heading>
          </GridItem>
          <GridItem colSpan="12" tabletColSpan="12">
            {loadingLocations && (
              <PendingContent loading loader={Spinner}>
                <div style={{ height: 100 }}></div>
              </PendingContent>
            )}
            {locationError && <ErrorBlock solid height={100} />}
            {locationData && (
              <LeafletMap
                markerCount={totalMarkers}
                markerGroups={markerGroups}
                height={500}
                zoomControl
                zoom={2}
                showHeatmapAtZoom={[0, 4]}
                showMarkersAtZoom={5}
              />
            )}
          </GridItem>

          <LocationsTables
            highNormalLocations={locationData?.locations.yellow || []}
            outlierLocations={locationData?.locations.red || []}
            loading={loadingLocations}
            error={Boolean(locationError)}
          />
          <GridItem colSpan="12" tabletColSpan="12">
            <InfoCard
              label={t('monitors.monitorInfo.monitorInfo')}
              gridData={infoGridData}
            />
          </GridItem>
          <GridItem colSpan="12" tabletColSpan="12">
            <PendingContent
              classes={(prev) => ({
                ...prev,
                root: `${prev.root} AutonomousMonitorDetails__trendChart`,
              })}
              loading={loadingGraphData}
              loader={Spinner}
            >
              <InfoCard
                label={t('monitors.autonomous.monitorDetails.monitorTrends')}
                headerRightContent={t('monitors.durationFilters.last7days')}
              >
                <LineChart
                  height={300}
                  data={graphData}
                  statusVariant="legend"
                  legendPosition="bottom"
                  groups={graphGroups}
                />
              </InfoCard>
            </PendingContent>
          </GridItem>
        </Grid>
      )}

      {monitorError && (
        <ErrorBlock message={t('genericErrorMessage')} height="75vh" />
      )}

      <Space size="l" />

      <SupportOutreachModal
        isOpen={supportOutreachModalOpen}
        onRequestClose={() => setSupportOutreachModalOpen(false)}
        monitorId={monitorId}
      />
    </div>
  );
};

export default memo(AutonomousMonitorDetails);
